import {
  TABLE_WATERMARK_DEFAULT_LEFT,
  TABLE_WATERMARK_IMAGE,
  TABLE_WATERMARK_LOGO_ATTR,
  TABLE_WATERMARK_MOBILE_MEDIA,
  TABLE_WATERMARK_ROW_STEP,
  getWatermarkLogoStyle,
} from 'styles/tableWatermark';
import { getWatermarkContainerConfig } from 'utils/tableWatermarkConfig';

const normalizeLeft = value => {
  if (value == null || value === '') {
    return TABLE_WATERMARK_DEFAULT_LEFT;
  }

  if (typeof value === 'number') {
    return `${value}px`;
  }

  const trimmed = String(value).trim();

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return `${trimmed}px`;
  }

  return trimmed;
};

// Source of truth is JS config (WeakMap), never DOM data-* attributes.
export const getWatermarkLeft = container => {
  const config = getWatermarkContainerConfig(container);
  const isMobile =
    typeof window !== 'undefined' &&
    window.matchMedia(TABLE_WATERMARK_MOBILE_MEDIA).matches;

  if (!config) {
    return TABLE_WATERMARK_DEFAULT_LEFT;
  }

  return normalizeLeft(isMobile ? config.leftMobile : config.left);
};

export const getWatermarkOffsetTopFromConfig = container => {
  const config = getWatermarkContainerConfig(container);

  if (!config || config.offsetTop == null) {
    return 8;
  }

  const offset = Number(config.offsetTop);

  return Number.isNaN(offset) ? 8 : offset;
};

export const createWatermarkLogo = () => {
  const logo = document.createElement('img');

  logo.setAttribute(TABLE_WATERMARK_LOGO_ATTR, 'true');
  logo.setAttribute('alt', '');
  logo.setAttribute('aria-hidden', 'true');
  logo.setAttribute('src', TABLE_WATERMARK_IMAGE);
  logo.draggable = false;

  return logo;
};

export const applyWatermarkLogoStyles = (logo, position) => {
  Object.entries(getWatermarkLogoStyle(position)).forEach(([key, value]) => {
    if (logo.style[key] !== value) {
      // eslint-disable-next-line no-param-reassign
      logo.style[key] = value;
    }
  });

  if (logo.getAttribute(TABLE_WATERMARK_LOGO_ATTR) !== 'true') {
    logo.setAttribute(TABLE_WATERMARK_LOGO_ATTR, 'true');
  }

  if (logo.getAttribute('alt') !== '') {
    logo.setAttribute('alt', '');
  }

  if (logo.getAttribute('aria-hidden') !== 'true') {
    logo.setAttribute('aria-hidden', 'true');
  }

  if (logo.getAttribute('src') !== TABLE_WATERMARK_IMAGE) {
    logo.setAttribute('src', TABLE_WATERMARK_IMAGE);
  }
};

export const syncWatermarkLogos = (container, layer) => {
  if (!container || !layer) {
    return;
  }

  const tables = container.querySelectorAll('table');

  if (!tables.length) {
    return;
  }

  // First data table only — shared watermark areas wrap several tables.
  const rows = tables[0].querySelectorAll('tbody tr');
  const rowCount = rows.length;

  if (!rowCount) {
    Array.from(
      layer.querySelectorAll(`[${TABLE_WATERMARK_LOGO_ATTR}]`)
    ).forEach(logo => {
      logo.remove();
    });
    return;
  }

  const left = getWatermarkLeft(container);
  const logos = Array.from(
    layer.querySelectorAll(`[${TABLE_WATERMARK_LOGO_ATTR}]`)
  );

  // Batch READ: only every Nth row (skip last border), one layer top.
  const layerTop = layer.getBoundingClientRect().top;
  const positions = [];

  for (let index = 0; index < rowCount; index += TABLE_WATERMARK_ROW_STEP) {
    if (index === rowCount - 1) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const rowRect = rows[index].getBoundingClientRect();

    if (rowRect.height <= 0) {
      // eslint-disable-next-line no-continue
      continue;
    }

    positions.push({
      top: rowRect.bottom - layerTop,
      left,
    });
  }

  // Batch WRITE: create / update / prune logos.
  positions.forEach((position, index) => {
    let logo = logos[index];

    if (!logo) {
      logo = createWatermarkLogo();
      layer.appendChild(logo);
    }

    applyWatermarkLogoStyles(logo, position);
  });

  logos.slice(positions.length).forEach(logo => {
    logo.remove();
  });
};
